/**
 * Email Verification Banner
 *
 * Soft banner shown at the top of the dashboard when the logged-in user
 * has not verified their email. They can:
 *   - Resend the verification email
 *   - Dismiss the banner for the current session
 *
 * Once verified (or dismissed), the banner is hidden.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Mail, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';
import { useAuth } from '@/context/AuthContext';

const SESSION_KEY = 'email_verify_banner_dismissed';

export const EmailVerificationBanner = () => {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(
    typeof window !== 'undefined' && sessionStorage.getItem(SESSION_KEY) === 'true'
  );
  const [resending, setResending] = useState(false);

  // Show only for authenticated users whose email is NOT verified
  if (!user || user.email_verified || dismissed) return null;

  const dismiss = () => {
    sessionStorage.setItem(SESSION_KEY, 'true');
    setDismissed(true);
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post('/auth/resend-verification', { email: user.email });
      toast.success(`Verification email sent to ${user.email}. Check your inbox.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resend verification email');
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg px-4 py-3 mb-4 flex items-center justify-between gap-4"
      data-testid="email-verification-banner"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
          <Mail className="w-4 h-4 text-amber-700" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-amber-900">
            Verify your email to unlock everything
          </p>
          <p className="text-xs text-amber-800 truncate">
            We sent a link to <span className="font-medium">{user.email}</span>. Until you click it, some features (transactional emails, billing receipts, password recovery) may be limited.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          size="sm"
          variant="outline"
          onClick={resend}
          disabled={resending}
          className="border-amber-300 text-amber-900 hover:bg-amber-100"
          data-testid="resend-verification-btn"
        >
          {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Resend email'}
        </Button>
        <button
          type="button"
          onClick={dismiss}
          className="p-1 rounded hover:bg-amber-100 text-amber-700"
          aria-label="Dismiss"
          data-testid="dismiss-verification-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
